<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex xs12>
        <!-- Header with actions -->
        <v-card>
          <v-card-title>
            <div>
              <h2>{{ aran.name }}</h2>
              <p class="text-muted">{{ aran.description }}</p>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="editAran"
              :loading="loading"
            >
              <v-icon left>edit</v-icon>
              ערוך
            </v-btn>
            <v-btn
              color="error"
              @click="deleteAran"
              :loading="loading"
              class="ml-2"
            >
              <v-icon left>delete</v-icon>
              מחק
            </v-btn>
          </v-card-title>
          
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 md6>
                <v-card outlined>
                  <v-card-title>פרטי הארן</v-card-title>
                  <v-card-text>
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>שם</v-list-item-title>
                          <v-list-item-subtitle>{{ aran.name }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>תיאור</v-list-item-title>
                          <v-list-item-subtitle>{{ aran.description || 'אין תיאור' }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>סטטוס</v-list-item-title>
                          <v-list-item-subtitle>
                            <v-chip
                              :color="aran.status === 'active' ? 'green' : 'red'"
                              text-color="white"
                              small
                            >
                              {{ aran.status === 'active' ? 'פעיל' : 'לא פעיל' }}
                            </v-chip>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>תאריך יצירה</v-list-item-title>
                          <v-list-item-subtitle>{{ formatDate(aran.createdAt) }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-flex>
              
              <v-flex xs12 md6>
                <v-card outlined>
                  <v-card-title>סטטיסטיקות</v-card-title>
                  <v-card-text>
                    <v-layout row wrap>
                      <v-flex xs6>
                        <div class="text-center">
                          <h3 class="display-1">{{ aran.events ? aran.events.length : 0 }}</h3>
                          <p>אירועים</p>
                        </div>
                      </v-flex>
                      <v-flex xs6>
                        <div class="text-center">
                          <h3 class="display-1">{{ activeEventsCount }}</h3>
                          <p>אירועים פעילים</p>
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>
      
      <!-- Events Section -->
      <v-flex xs12 class="mt-4">
        <v-card>
          <v-card-title>
            <h3>אירועים של הארן</h3>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="חיפוש באירועים"
              single-line
              hide-details
              style="max-width: 300px;"
            ></v-text-field>
          </v-card-title>
          
          <v-card-text>
            <v-data-table
              :headers="eventHeaders"
              :items="filteredEvents"
              :loading="loading"
              :search="search"
              class="elevation-1"
              :rows-per-page-items="[10, 25, 50]"
              rows-per-page-text="שורות בעמוד:"
            >
              <template slot="items" slot-scope="props">
                <td>
                  <v-btn
                    text
                    color="primary"
                    @click="goToEvent(props.item._id)"
                  >
                    {{ props.item.name }}
                  </v-btn>
                </td>
                
                <td>{{ props.item.description || 'אין תיאור' }}</td>
                
                <td>
                  <v-chip
                    :color="props.item.status === 'active' ? 'green' : 'red'"
                    text-color="white"
                    small
                  >
                    {{ props.item.status === 'active' ? 'פעיל' : 'לא פעיל' }}
                  </v-chip>
                </td>
                
                <td>{{ formatDate(props.item.createdAt) }}</td>
                
                <td>
                  <v-btn
                    icon
                    small
                    @click="goToEvent(props.item._id)"
                    color="primary"
                  >
                    <v-icon>visibility</v-icon>
                  </v-btn>
                </td>
              </template>
              
              <template slot="no-data">
                <v-alert :value="true" type="info">
                  לא נמצאו אירועים לארן זה
                </v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">ערוך ארן</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="editedAran.name"
              label="שם הארן"
              :rules="[v => !!v || 'שם הארן הוא שדה חובה']"
              required
            ></v-text-field>
            
            <v-text-field
              v-model="editedAran.description"
              label="תיאור"
              multi-line
              rows="3"
            ></v-text-field>
            
            <v-select
              v-model="editedAran.status"
              :items="statusOptions"
              label="סטטוס"
              required
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            flat
            @click="editDialog = false"
          >
            ביטול
          </v-btn>
          <v-btn
            color="blue darken-1"
            flat
            @click="saveAran"
            :loading="saving"
            :disabled="!valid"
          >
            שמור
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">מחיקת ארן</v-card-title>
        <v-card-text>
          האם אתה בטוח שברצונך למחוק את הארן "{{ aran.name }}"?
          פעולה זו לא ניתנת לביטול.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            flat
            @click="deleteDialog = false"
          >
            ביטול
          </v-btn>
          <v-btn
            color="red darken-1"
            flat
            @click="confirmDelete"
            :loading="deleting"
          >
            מחק
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { axiosInstance } from '@/api';

export default {
  name: 'AranDetail',
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      editDialog: false,
      deleteDialog: false,
      valid: false,
      search: '',
      aran: {
        name: '',
        description: '',
        status: 'active',
        events: [],
        createdAt: null
      },
      editedAran: {
        name: '',
        description: '',
        status: 'active'
      },
      eventHeaders: [
        { text: 'שם האירוע', value: 'name', sortable: true },
        { text: 'תיאור', value: 'description', sortable: false },
        { text: 'סטטוס', value: 'status', sortable: true },
        { text: 'תאריך יצירה', value: 'createdAt', sortable: true },
        { text: 'פעולות', value: 'actions', sortable: false }
      ],
      statusOptions: [
        { text: 'פעיל', value: 'active' },
        { text: 'לא פעיל', value: 'inactive' }
      ]
    };
  },

  computed: {
    filteredEvents() {
      if (!this.aran.events) return [];
      if (!this.search) return this.aran.events;
      
      return this.aran.events.filter(event => 
        event.name.toLowerCase().includes(this.search.toLowerCase()) ||
        (event.description && event.description.toLowerCase().includes(this.search.toLowerCase()))
      );
    },
    
    activeEventsCount() {
      if (!this.aran.events) return 0;
      return this.aran.events.filter(event => event.status === 'active').length;
    }
  },

  mounted() {
    this.fetchAran();
  },

  methods: {
    async fetchAran() {
      this.loading = true;
      try {
        // Using the correct aran API endpoint
        const response = await axiosInstance.get(`/aran/${this.$route.params.id}`);
        const aran = response.data.data || response.data || {};
        // Transform aran data to include missing fields
        this.aran = {
          _id: aran._id,
          name: aran.name,
          description: aran.description || 'אין תיאור',
          status: aran.status || 'active',
          events: aran.events || [], // Events will be empty for now
          createdAt: aran.createdAt,
          updatedAt: aran.updatedAt
        };
        this.editedAran = { ...this.aran };
      } catch (error) {
        console.error('Error fetching aran:', error);
        this.$toast.error('שגיאה בטעינת הארן');
      } finally {
        this.loading = false;
      }
    },

    editAran() {
      this.editedAran = { ...this.aran };
      this.editDialog = true;
    },

    async saveAran() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        // Using the correct aran API endpoint
        await axiosInstance.put(`/aran/${this.aran._id}`, {
          name: this.editedAran.name,
          description: this.editedAran.description,
          status: this.editedAran.status
        });
        this.$toast.success('הארן עודכן בהצלחה');
        this.editDialog = false;
        this.fetchAran();
      } catch (error) {
        console.error('Error saving aran:', error);
        this.$toast.error('שגיאה בשמירת הארן');
      } finally {
        this.saving = false;
      }
    },

    deleteAran() {
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        // Using the correct aran API endpoint
        await axiosInstance.delete(`/aran/${this.aran._id}`);
        this.$toast.success('הארן נמחק בהצלחה');
        this.$router.push('/arans');
      } catch (error) {
        console.error('Error deleting aran:', error);
        this.$toast.error('שגיאה במחיקת הארן');
      } finally {
        this.deleting = false;
      }
    },

    goToEvent(eventId) {
      this.$router.push(`/events/${eventId}`);
    },

    formatDate(date) {
      if (!date) return 'לא ידוע';
      return new Date(date).toLocaleDateString('he-IL');
    }
  }
};
</script>

<style scoped>
.v-data-table {
  direction: rtl;
}

.v-chip {
  cursor: pointer;
}

.v-chip:hover {
  opacity: 0.8;
}
</style> 


// WEBPACK FOOTER //
// src/views/AranDetail.vue